import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { DocTreeItem, SIDEBAR_DOC_LEVELS, SIDEBAR_DOC_ROW } from './DocTreeItem'
import { DropLine } from './SidebarRows'
import {
  DOC_DWELL_MS,
  EXTERNAL_GRAB_OFFSET_PX,
  ancestorIds,
  depthFromPointer,
  flattenVisibleDocs,
  resolveEndDrop,
  resolveInsideDrop,
  resolveLineDrop,
  rowZone,
} from './sidebarDnd'
import type { ReactNode } from 'react'
import type { DocDrop, DocDropSource, VisibleDocRow } from './sidebarDnd'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { PageReorderEvent } from '@/routes/features/pages/hooks/usePageReorder'
import { subtreeHeight } from '@/routes/features/pages/utils/docMove'
import { createDragExtractor } from '@/lib/utils/dragDrop'

const extractPageDragData = createDragExtractor('page')
const BASE_INDENT_PX = 4

function sameDrop(a: DocDrop | null, b: DocDrop | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.mode === b.mode &&
    a.depth === b.depth &&
    a.parentId === b.parentId &&
    a.targetId === b.targetId &&
    a.position === b.position &&
    a.lineRowId === b.lineRowId &&
    a.lineEdge === b.lineEdge
  )
}

interface SpaceDocTreeProps {
  /** Every live doc in the space. */
  spaceDocs: ReadonlyArray<PageDocument>
  /** Every doc in the workspace, for drags that start outside the space. */
  allPages: ReadonlyArray<PageDocument>
  /** The top-level docs the sidebar shows. */
  roots: ReadonlyArray<PageDocument>
  workspaceSlug: string
  spaceKey: string
  /** Set when these are a project's docs rather than the space's own. */
  projectKey?: string
  currentPath: string
  pagesBasePath: string
  onCreateSubPage: (parentId: string) => void
  /** Reorder or re-nest a doc that already lives in the space. */
  onReorder: (event: PageReorderEvent) => void
  /** Bring a doc in from a project (or elsewhere) as a space doc. */
  onMoveIn: (docId: string, parentPageId: string | null) => void
  /** Rendered under the docs, inside the drop area (the "All docs" row). */
  footer?: ReactNode
  /** Left inset of the top level, e.g. to sit under a project row. */
  baseIndentPx?: number
}

/**
 * The space's docs in the sidebar with drag and drop (design 22c). The line's
 * indent is the level a doc lands at — move right to nest, left to un-nest;
 * holding over a doc for 500ms outlines and expands it so you can drop inside.
 * The rules live in sidebarDnd.ts; this measures the pointer and renders.
 */
export function SpaceDocTree({
  spaceDocs,
  allPages,
  roots,
  workspaceSlug,
  spaceKey,
  projectKey,
  currentPath,
  pagesBasePath,
  onCreateSubPage,
  onReorder,
  onMoveIn,
  footer,
  baseIndentPx = BASE_INDENT_PX,
}: SpaceDocTreeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set())
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [drop, setDrop] = useState<DocDrop | null>(null)

  const rows = useMemo(
    () => flattenVisibleDocs(spaceDocs, roots, expandedIds, SIDEBAR_DOC_LEVELS),
    [spaceDocs, roots, expandedIds],
  )
  const docById = useMemo(() => new Map(spaceDocs.map((d) => [d.id, d])), [spaceDocs])

  // Open the path down to the doc you're on.
  const activeSlug = currentPath.startsWith(`${pagesBasePath}/`)
    ? (currentPath.slice(pagesBasePath.length + 1).split('/')[0] ?? null)
    : null
  const activeId = activeSlug ? (spaceDocs.find((d) => d.slug === activeSlug)?.id ?? null) : null
  useEffect(() => {
    if (!activeId) return
    const path = ancestorIds(spaceDocs, activeId)
    if (path.length === 0) return
    setExpandedIds((prev) => (path.every((id) => prev.has(id)) ? prev : new Set([...prev, ...path])))
  }, [activeId, spaceDocs])

  const toggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Latest values for the drag handlers, which stay registered for the whole drag.
  const latest = useRef({ rows, spaceDocs, allPages, onReorder, onMoveIn, baseIndentPx })
  latest.current = { rows, spaceDocs, allPages, onReorder, onMoveIn, baseIndentPx }
  const dropRef = useRef<DocDrop | null>(null)
  const grabRef = useRef<{ id: string; offset: number } | null>(null)
  const dwellRef = useRef<{ id: string; ready: boolean; timer: ReturnType<typeof setTimeout> } | null>(null)

  const clearDwell = () => {
    if (dwellRef.current) clearTimeout(dwellRef.current.timer)
    dwellRef.current = null
  }
  const show = (next: DocDrop | null) => {
    if (sameDrop(next, dropRef.current)) return
    dropRef.current = next
    setDrop(next)
  }

  const onRowDragStart = useCallback((id: string, grabOffsetX: number) => {
    grabRef.current = { id, offset: grabOffsetX }
    setDraggingId(id)
  }, [])
  const onRowDragEnd = useCallback(() => {
    grabRef.current = null
    setDraggingId(null)
  }, [])

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const sourceFor = (id: string): DocDropSource => {
      const { spaceDocs: docs, allPages: pages } = latest.current
      const inTree = docs.some((d) => d.id === id)
      const doc = pages.find((d) => d.id === id)
      return { id, inTree, parentId: doc?.parentPageId ?? null, height: subtreeHeight(pages, id) }
    }

    const reset = () => {
      clearDwell()
      show(null)
    }

    const update: Parameters<typeof dropTargetForElements>[0]['onDrag'] = ({ source, location }) => {
      const data = extractPageDragData(source.data)
      if (!data) return
      const { rows: visible, spaceDocs: docs } = latest.current
      const src = sourceFor(data.id)
      const inner = location.current.dropTargets[0]
      const { clientX, clientY } = location.current.input

      const rowId = inner && inner.data[SIDEBAR_DOC_ROW] === true && typeof inner.data.id === 'string' ? inner.data.id : null
      if (!inner || !rowId) {
        clearDwell()
        show(resolveEndDrop({ docs, rows: visible, source: src }))
        return
      }

      const rect = inner.element.getBoundingClientRect()
      const zone = rowZone(clientY, rect)
      if (!zone.middle) {
        clearDwell()
      } else if (dwellRef.current?.id !== rowId) {
        clearDwell()
        const timer = setTimeout(() => {
          if (dwellRef.current?.id !== rowId) return
          dwellRef.current.ready = true
          const row = latest.current.rows.find((r: VisibleDocRow) => r.id === rowId)
          if (row?.canExpand && !row.isExpanded) setExpandedIds((prev) => new Set([...prev, rowId]))
          // Show it now rather than waiting for the next dragover, which a still pointer may not send.
          const inside = resolveInsideDrop({ docs: latest.current.spaceDocs, targetId: rowId, source: src })
          if (inside) show(inside)
        }, DOC_DWELL_MS)
        dwellRef.current = { id: rowId, ready: false, timer }
      }

      if (zone.middle && dwellRef.current?.id === rowId && dwellRef.current.ready) {
        const inside = resolveInsideDrop({ docs, targetId: rowId, source: src })
        if (inside) {
          show(inside)
          return
        }
      }

      const grab = grabRef.current
      show(
        resolveLineDrop({
          docs,
          rows: visible,
          hoveredId: rowId,
          edge: zone.edge,
          desiredDepth: depthFromPointer({
            pointerX: clientX,
            rowLeft: rect.left,
            baseIndentPx: latest.current.baseIndentPx,
            grabOffsetX: grab?.id === data.id ? grab.offset : EXTERNAL_GRAB_OFFSET_PX,
          }),
          source: src,
        }),
      )
    }

    return combine(
      dropTargetForElements({
        element,
        canDrop: ({ source }) => extractPageDragData(source.data) !== null,
        getData: () => ({ sidebarDocTree: true }),
        onDragEnter: update,
        onDrag: update,
        onDragLeave: reset,
        onDrop: ({ source }) => {
          const data = extractPageDragData(source.data)
          const result = dropRef.current
          reset()
          if (!data || !result) return
          const src = sourceFor(data.id)
          if (src.inTree) {
            if (result.targetId) {
              latest.current.onReorder({ sourceId: data.id, targetId: result.targetId, dropPosition: result.position })
            }
            return
          }
          latest.current.onMoveIn(data.id, result.parentId)
        },
      }),
      monitorForElements({ onDrop: reset }),
    )
    // Handlers read everything through refs, so the target registers once.
  }, [])

  useEffect(() => () => clearDwell(), [])

  return (
    <div ref={containerRef} className="relative flex flex-col gap-px px-2" data-testid="sidebar-space-docs">
      {rows.map((row) => {
        const doc = docById.get(row.id)
        if (!doc) return null
        return (
          <DocTreeItem
            key={row.id}
            doc={doc}
            row={row}
            workspaceSlug={workspaceSlug}
            spaceKey={spaceKey}
            projectKey={projectKey}
            currentPath={currentPath}
            pagesBasePath={pagesBasePath}
            baseIndentPx={baseIndentPx}
            isDragging={draggingId === row.id}
            dropLine={drop?.mode === 'line' && drop.lineRowId === row.id ? { edge: drop.lineEdge, depth: drop.depth } : null}
            isDropParent={drop !== null && drop.parentId === row.id}
            onToggle={toggle}
            onCreateSubPage={onCreateSubPage}
            onDragStart={onRowDragStart}
            onDragEnd={onRowDragEnd}
          />
        )
      })}
      {drop?.mode === 'line' && drop.lineRowId === null && (
        <div className="relative h-0">
          <DropLine edge="top" indentPx={baseIndentPx + 8} />
        </div>
      )}
      {footer}
    </div>
  )
}
